/**
 * webauthn-errors/messages.ts — DOMException and server error code message maps
 *
 * Error philosophy:
 *  - Never say "error" without telling the user what to do
 *  - Amber (#F59E0B) for user-correctable errors (colour semantics)
 *  - Red (#EF4444) is reserved for Forensic Pause and critical OSINT alerts
 *  - Security errors (clone detection, MDS failure) remain vague to attackers
 */

import { DEVICE_LIMIT_MESSAGE } from "@shared/webauthn-device-limits";
import type { WebAuthnUserMessage } from "./types";
import { PROTECTION_LEVELS } from "@/lib/terminology";
import { MASTER_ERROR_MESSAGES } from "./master-messages";
import { KEYPAIR_BINDING_ERROR_MESSAGES } from "./keypair-binding-messages";
import { CHALLENGE_RACE_ERROR_MESSAGES } from "./challenge-race-messages"; // AC-15
import { ORG_ADMIN_ERROR_MESSAGES } from "./org-admin-messages"; // #2999
import { SESSION_LIFECYCLE_ERROR_MESSAGES } from "./session-lifecycle-messages"; // ledger #130
import { FORENSIC_CONTAINMENT_ERROR_MESSAGES } from "./forensic-containment-messages"; // #3082

// DOMException name → user message

/** Map of WebAuthn DOMException.name values to user messages */
export const DOM_EXCEPTION_MESSAGES: Record<string, WebAuthnUserMessage> = {

  NotAllowedError: {
    title:       "Request cancelled or timed out",
    description: "You cancelled the request or it timed out. Tap your security key or use your device biometric when prompted.",
    retryable:   true,
    severity:    "amber",
  },

  SecurityError: {
    title:       "Wrong website address",
    description: "This page's address does not match the website address your security key was set up for. Go to the correct website and try again.",
    retryable:   false,
    severity:    "amber",
  },

  // `excludeCredentials` found an enrolled credential. This fires for every
  // add-device mode, so the copy must not assume a removable security key.
  InvalidStateError: {
    title:       "Already registered",
    description: "This device or security key already has a passkey for your account, so there is nothing to add. To add a different one, go back and choose another option.",
    retryable:   true,
    severity:    "amber",
  },

  NotSupportedError: {
    title:       "Passkey not supported",
    description: "Your device or browser does not support passkeys. Try a different browser (Chrome, Firefox, Edge, Safari) or use a USB security key.",
    retryable:   false,
    severity:    "amber",
  },

  UnknownError: {
    title:       "Your security key had a problem",
    description: "An unexpected error occurred with your security key. Remove it, re-insert it, and try again. If the issue persists, try a different key.",
    retryable:   true,
    severity:    "amber",
  },

  AbortError: {
    title:       "Request cancelled",
    description: "The sign-in was cancelled. Please try again when you are ready.",
    retryable:   true,
    severity:    "amber",
  },

  ConstraintError: {
    title:       "Security key does not meet requirements",
    description: `This security key does not meet the requirements for ${PROTECTION_LEVELS.AAL3.label}. Please use an attested hardware key such as a YubiKey 5 or similar.`,
    retryable:   false,
    severity:    "amber",
  },

  DataError: {
    title:       "That didn't go through",
    description: "Something in the sign-in response wasn't readable. Try again.",
    retryable:   true,
    severity:    "amber",
  },

  EncodingError: {
    title:       "That didn't go through",
    description: "The request couldn't be sent properly. Refresh the page and try again.",
    retryable:   true,
    severity:    "amber",
  },
};

// Server error code → user message

export const SERVER_ERROR_MESSAGES: Record<string, WebAuthnUserMessage> = {

  // Still emitted by authentication.ts, challenge-respond.ts and the
  // batch-sign route — kept mapped alongside the registration-specific code
  // below (registration.ts renamed ITS use to REGISTRATION_RESTART_REQUIRED).
  INVALID_CHALLENGE: {
    title:       "Session expired",
    description: "Your sign-in session expired before you completed the request. Please start over.",
    retryable:   true,
    severity:    "amber",
  },

  // The most common benign registration failure (expired/consumed challenge).
  // Without this entry it fell through to the generic fallback message.
  REGISTRATION_RESTART_REQUIRED: {
    title:       "Session expired",
    description: "Your registration session expired before you completed the request. Please start over.",
    retryable:   true,
    severity:    "amber",
  },

  // Emitted when an assertion was minted for a different ceremony — a
  // recoverable state (start the confirmation again), never a security claim.
  CHALLENGE_BINDING_MISMATCH: {
    title:       "Confirmation did not match",
    description: "That passkey confirmation was for a different request. Please start over.",
    retryable:   true,
    severity:    "amber",
  },

  VERIFICATION_FAILED: {
    title:       "Verification failed",
    description: "We could not verify your security key. Make sure you are using the correct key for this account.",
    retryable:   true,
    severity:    "amber",
  },

  UV_REQUIRED: {
    title:       "PIN or biometric required",
    description: "Your authenticator must have a PIN or biometric configured. Please set one up in your device settings, then try again.",
    retryable:   true,
    severity:    "amber",
  },

  INSUFFICIENT_AAL: {
    title:       "Sign in again to continue",
    description: "This action needs a stronger sign-in. Sign out, then sign in again before trying again.",
    retryable:   false,
    severity:    "amber",
  },

  // Credential-APPEND fresh-UV gate (#2210 unified device-add, register-append-uv.ts).
  // Distinct from UV_REQUIRED above: that means "no PIN/biometric configured"
  // (device settings); this means "prove you already control this account
  // before adding a credential" (confirm with an EXISTING passkey instead).
  APPEND_UV_REQUIRED: {
    title:       "Confirm with an existing passkey",
    description: "Confirm with a passkey already on this account to add a new one.",
    retryable:   true,
    severity:    "amber",
  },

  CREDENTIAL_NOT_FOUND: {
    title:       "Security key not recognised",
    description: "This security key is not registered with your account. Please use a registered key, or register a new one.",
    retryable:   true,
    severity:    "amber",
  },

  CREDENTIAL_REVOKED: {
    title:       "Security key revoked",
    description: "This security key has been removed from your account. Please use a different registered key.",
    retryable:   true,
    severity:    "amber",
  },

  COUNTER_ANOMALY: {
    title:       "We stopped this request",
    description: "A security issue was detected with your authenticator. Your account has been protected. Please contact support.",
    retryable:   false,
    severity:    "red",
  },

  AAGUID_MISSING: {
    title:       "Hardware security key required",
    description: `${PROTECTION_LEVELS.AAL3.label} requires an attested hardware key. Your current key does not provide the required identity data. Please use a hardware key (e.g. YubiKey 5, Titan key).`,
    retryable:   false,
    severity:    "amber",
  },

  MDS_NOT_FOUND: {
    title:       "Security key not recognised",
    description: `Your security key is not in the known-good registry. ${PROTECTION_LEVELS.AAL3.label} requires a registered, attested hardware key.`,
    retryable:   false,
    severity:    "amber",
  },

  MDS_NOT_HARDWARE: {
    title:       "Hardware security key required",
    description: `${PROTECTION_LEVELS.AAL3.label} requires a hardware security key. Software and virtual keys are not accepted.`,
    retryable:   false,
    severity:    "amber",
  },

  MDS_REVOKED: {
    title:       "Security key has a known vulnerability",
    description: "This model of security key has been flagged in the security key registry. Please use a different, up-to-date hardware key.",
    retryable:   false,
    severity:    "red",
  },

  // NO_HARDWARE_KEY means the account HAS active keys, none of them attested —
  // NOT that it has none (that is NO_CREDENTIALS). The old copy here told a
  // person holding four working keys to "register a USB or NFC security key",
  // which is the exact false diagnosis behind "it never remembers my key"
  // (#2207). Copy kept in lockstep with use-step-up-aal3.ts's mapStepUpError.
  NO_HARDWARE_KEY: {
    title:       "Attested hardware key required",
    description: "The keys on your account sign you in, but none of them is an attested hardware key — only an attested hardware key unlocks this. Add one in Settings to continue.",
    retryable:   false,
    severity:    "amber",
  },

  NO_CREDENTIALS: {
    title:       "No security key registered",
    description: "No security key is registered on your account. Add one in Settings to continue.",
    retryable:   false,
    severity:    "amber",
  },

  LAST_CREDENTIAL: {
    title:       "Cannot remove last security key",
    description: "You cannot remove your last registered security key — you would be locked out. Register a new key first, then remove the old one.",
    retryable:   false,
    severity:    "amber",
  },

  // Device cap (AC-1/AC-3, #1604) — names the limit and points at Security
  // Centre removal. Amber: a user-correctable state, not a security event.
  // Copy interpolated from the shared constant so an owner-steered cap
  // (3–5) can never drift from what the user is told.
  DEVICE_LIMIT_REACHED: {
    title:       "Device limit reached",
    description: DEVICE_LIMIT_MESSAGE,
    retryable:   false,
    severity:    "amber",
  },

  // Append-UV gate (webauthn-register-append-uv.routes.ts) — adding a device
  // requires confirming with an ALREADY-enrolled passkey first. Without this
  // entry the fallback told users to "refresh the page", unrelated to the cause.
  UV_FAILED: {
    title:       "Confirmation didn't complete",
    description: "Confirm with a passkey that's already on your account, then try adding the new device again.",
    retryable:   true,
    severity:    "amber",
  },

  // Anti-enumeration: REGISTRATION_UNAVAILABLE replaces USERNAME_TAKEN
  // so the error code itself doesn't confirm account existence.
  REGISTRATION_UNAVAILABLE: {
    title:       "Unable to create account",
    description: "Registration could not be completed with that username. If you already have an account, use Sign in to continue with your passkey.",
    retryable:   true,
    severity:    "amber",
  },
  USERNAME_TAKEN: {
    title:       "Unable to create account",
    description: "Registration could not be completed with that username. If you already have an account, use Sign in to continue with your passkey.",
    retryable:   true,
    severity:    "amber",
  },

  PRF_NOT_SUPPORTED: {
    title:       "Passkey does not support vault encryption",
    description: "Your security key or browser does not support the extension required to unlock your vault. Please use a recent YubiKey or a compatible passkey with Chrome or Firefox.",
    retryable:   false,
    severity:    "amber",
  },

  // Split-out tables (300-line rule): master #1605, keypair #90, race AC-15,
  // org-admin #2999, session-lifecycle (the 2026-09-02 merge product, ledger #130).
  ...MASTER_ERROR_MESSAGES,
  ...KEYPAIR_BINDING_ERROR_MESSAGES,
  ...CHALLENGE_RACE_ERROR_MESSAGES,
  ...ORG_ADMIN_ERROR_MESSAGES,
  ...SESSION_LIFECYCLE_ERROR_MESSAGES,
  ...FORENSIC_CONTAINMENT_ERROR_MESSAGES,
};

// Fallback message

export const FALLBACK_MESSAGE: WebAuthnUserMessage = {
  // #3386: a bare "Something went wrong" title names no action (DESIGN-SYSTEM
  // §7.1 rule 5) — the description below already carries the action verb, so
  // only the title changes here, to a short, non-blaming noun phrase that
  // matches this file's other entries' style (e.g. "Session expired").
  title:       "Unexpected problem",
  description: "An unexpected issue occurred. Please refresh the page and try again. If the problem continues, contact support.",
  retryable:   true,
  severity:    "amber",
};
